import React, { useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { PlusIcon } from "lucide-react";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import {
  createExerciseInputTypes,
  ExerciseResponseServerViewType,
  ExerciseResponseViewType,
} from "@/types/exercise";
import { ErrorType } from "@/types";
import {
  useGetAllCategoryQuery,
  useGetAllExercisesQuery,
} from "@/services/exerciseApi";
import ExerciseForm from "../modal/form";

import { DataTableRowActions } from "./data-table-row-actions";
import {
  difficultyTypeoptions,
  exerciseTypeOptions,
  visibilityOptions,
} from "@/lib/constants/exercise";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import TableFilters from "@/components/ui/table/data-table-filter";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import useTableData from "@/components/ui/table/data-table-logic";

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
  search_key?: string;
  category?: string[];
  visible_for?: string[];
  difficulty?: string;
  exercise_type?: string;
  type: string;
}
const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: "exercise",
};

export default function ExerciseTableView() {
  const exercise = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).exercise ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const {
    searchCriteria,
    query,
    toggleSortOrder,
    setSearchCriteria,
    inputValue,
    setInputValue,
  } = useTableData(initialValue);
  const [data, setData] = useState<ExerciseResponseServerViewType | undefined>(
    undefined
  );

  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [action, setAction] = useState<"add" | "edit">("add");

  const { data: CategoryData } = useGetAllCategoryQuery();

  const {
    data: exercisedata,
    isLoading,
    refetch,
    error,
    isError,
  } = useGetAllExercisesQuery(
    {
      org_id: orgId,
      query: query,
    },
    {
      skip: query == "",
    }
  );

  React.useEffect(() => {
    if (isError && navigator.onLine) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: typedError.data?.detail ?? typedError.data?.message,
      });
    }
  }, [isError]);

  const exerciseTableData = React.useMemo(() => {
    return Array.isArray(exercisedata?.data) ? exercisedata?.data : [];
  }, [exercisedata]);

  function handleRoute() {
    setAction("add");
    setIsDialogOpen(true);
  }
  const { toast } = useToast();

  const [sorting, setSorting] = useState<SortingState>([]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const displayValue = (value: string | undefined | null) =>
    value == null || value == undefined || value.trim() == "" ? "" : value;

  const handleEditExercise = (data: ExerciseResponseServerViewType) => {
    setData(data);
    setAction("edit");
    setIsDialogOpen(true);
  };

  const actionsColumn: ColumnDef<ExerciseResponseViewType> = {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <DataTableRowActions
        access={exercise}
        row={row.original.id}
        data={row?.original}
        refetch={refetch}
        hanleEditExercise={handleEditExercise}
      />
    ),
  };

  const columns: ColumnDef<ExerciseResponseViewType>[] = [
    {
      accessorKey: "exercise_name",
      meta: "Exercise Name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Exercise Name"
          sortKey="exercise_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex px-2 text-ellipsis whitespace-nowrap overflow-hidden">
            {/* {displayValue(row?.original?.exercise_name)} */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="capitalize cursor-pointer">
                    {/* Display the truncated name */}
                    {displayValue(
                      `${row.original.exercise_name}`.length > 8
                        ? `${row.original.exercise_name}`.substring(0, 8) +
                            "..."
                        : `${row.original.exercise_name}`
                    )}
                  </p>
                </TooltipTrigger>
                <TooltipContent>
                  {/* Display the full name in the tooltip */}
                  <p>{displayValue(`${row?.original?.exercise_name}`)}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
    },
    {
      accessorKey: "category_name",
      meta: "Exercise Category",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Exercise Category"
          sortKey="category_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex px-2 text-ellipsis whitespace-nowrap overflow-hidden">
            {row.original.category_name}
          </div>
        );
      },
    },
    {
      accessorKey: "visible_for",
      meta: "Visible For",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Visible For"
          sortKey="visible_for"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex px-2 text-ellipsis whitespace-nowrap overflow-hidden">
            {row.original.visible_for}
          </div>
        );
      },
    },
    {
      accessorKey: "exercise_type",
      meta: "Exercise Type",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Exercise Type"
          sortKey="exercise_type"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex px-2 text-ellipsis whitespace-nowrap overflow-hidden">
            {row.original.exercise_type}{" "}
          </div>
        );
      },
    },
    {
      accessorKey: "difficulty",
      meta: "Difficulty",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Difficulty"
          sortKey="difficulty"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex  px-2 text-ellipsis whitespace-nowrap overflow-hidden">
            {row.original.difficulty}{" "}
          </div>
        );
      },
    },
    {
      accessorKey: "sets",
      meta: "Sets",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Sets"
          sortKey="set"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex  px-2 text-ellipsis whitespace-nowrap overflow-hidden">
            {row.original.sets}{" "}
          </div>
        );
      },
    },
    ...(exercise !== "read" ? [actionsColumn] : []),
  ];

  const table = useReactTable({
    data: exerciseTableData as ExerciseResponseViewType[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  function handleFilterChange(field: string, value: string | number) {
    setFilterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  const filterDisplay = [
    {
      type: "multiselect",
      name: "category",
      label: "Exercise category",
      options: CategoryData,
      function: (value: string) => handleFilterChange("category", value),
    },
    {
      type: "multiselect",
      name: "visible_for",
      label: "Visible for",
      options: visibilityOptions.map((item) => ({
        value: item.value,
        label: item.label,
      })),
      function: (value: string) => handleFilterChange("visible_for", value),
    },
    {
      type: "select",
      name: "exercise_type",
      label: "Exercise Type",
      options: exerciseTypeOptions.map((item) => ({
        id: item.value,
        name: item.label,
      })),
      function: (value: string) => handleFilterChange("exercise_type", value),
    },
    {
      type: "select",
      name: "difficulty",
      label: "difficulty",
      options: difficultyTypeoptions.map((item) => ({
        id: item.label,
        name: item.label,
      })),
      function: (value: string) => handleFilterChange("difficulty", value),
    },
  ];
  // Function to go to the next page

  const totalRecords = exercisedata?.filtered_counts ?? 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-row items-center justify-between gap-2 px-3">
        {/* Table Filter component */}
        <div className="flex-initial sm:flex-1 min-w-0">
          <TableSearchFilter
            setSearchCriteria={setSearchCriteria}
            inputValue={inputValue}
            setInputValue={setInputValue}
          />
        </div>

        {/* Buttons Container */}
        <div className="flex flex-row items-center gap-1 sm:gap-2 shrink-0">
          {exercise !== "read" && (
            <Button
              className="bg-primary text-sm text-black flex items-center justify-center h-8 px-2 min-w-[32px]"
              onClick={handleRoute}
            >
              <PlusIcon className="size-4" />
              <span className="hidden sm:inline ml-1">Create New</span>
            </Button>
          )}
          {/* <DataTableViewOptions table={table} /> */}
          <button
            className={`border rounded-full size-8 text-gray-400 flex items-center justify-center shrink-0 ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-4 px-3">
        {isLoading ? (
          <div className="flex space-x-2 justify-center items-center h-24">
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce"></div>
          </div>
        ) : exerciseTableData.length > 0 ? (
          exerciseTableData.map((row: any) => (
            <div
              key={row.id}
              className="bg-white border rounded-lg p-4 shadow-sm relative space-y-2"
              onClick={() => exercise !== "read" && handleEditExercise(row)}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-bold text-lg capitalize">{row.exercise_name}</h3>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <DataTableRowActions
                    access={exercise}
                    row={row.id}
                    data={row}
                    refetch={refetch}
                    hanleEditExercise={handleEditExercise}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm pt-2">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-list text-gray-400 w-4"></i>
                  <span>Category: {row.category_name || "N/A"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-eye text-gray-400 w-4"></i>
                  <span>Visible For: {row.visible_for || "N/A"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-dumbbell text-gray-400 w-4"></i>
                  <span>Type: {row.exercise_type || "N/A"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-gauge text-gray-400 w-4"></i>
                  <span>Difficulty: {row.difficulty || "N/A"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-layer-group text-gray-400 w-4"></i>
                  <span>Sets: {row.sets || 0}</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="h-24 text-center flex items-center justify-center border rounded-lg">
            No Exercise found.
          </div>
        )}
      </div>

      <div className="hidden md:block rounded-none border border-border ">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          {/* <Table className="w-full overflow-x-scroll"> */}
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={(e) => {
                      if (
                        exercise !== "read" &&
                        e.target instanceof Element &&
                        !e.target.closest('[role="checkbox"]') &&
                        (e.target === e.currentTarget || e.target.closest("td"))
                      ) {
                        handleEditExercise(row.original as unknown as ExerciseResponseServerViewType);
                      }
                    }}
                    className={
                      exercise !== "read"
                        ? "cursor-pointer hover:bg-secondary"
                        : ""
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : exercisedata?.total_counts == 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No Exercise added yet.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No records for the criteria.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* pagination */}
      {exerciseTableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}
      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />

      <ExerciseForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        action={action}
        setAction={setAction}
        data={data}
        setData={setData}
        refetch={refetch}
      />
    </div>
  );
}
